"use client";

import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { Bell, User } from "lucide-react";
import { useAuth } from "../../lib/hooks/useAuth";
import { useSession } from "next-auth/react";
import {
  subscribeToUnreadNotificationCount,
} from "../../lib/api/notifications";
import { NotificationDropdown } from "../notifications/NotificationDropdown";
import { ProfileDropdown } from "../profile/ProfileDropdown";
import { getFirebaseAuth } from "../../lib/api/firebase-ultra-safe";

export function Header() {
  const { user, loading } = useAuth();
  const { data: session } = useSession();
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // 로딩 중이거나 사용자가 없으면 알림 구독하지 않음
    if (loading || !user?.uid) {
      setUnreadNotificationCount(0);
      return;
    }

    // Firebase Auth 상태 확인 (NextAuth만으로는 Firestore 권한이 없음)
    let unsubscribeUnreadCount: (() => void) | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    // 약간의 지연을 두어 로그인 상태가 안정화될 때까지 대기
    timeoutId = setTimeout(() => {
      try {
        const auth = getFirebaseAuth();
        const firebaseUser = auth?.currentUser;
        
        // Firebase Auth에 로그인되어 있지 않으면 알림 구독하지 않음
        // (NextAuth만으로는 Firestore 권한이 없어서 권한 오류 발생)
        if (!firebaseUser) {
          console.log("⚠️ Firebase Auth 미로그인 - 알림 구독 건너뜀 (NextAuth만으로는 Firestore 권한 없음)");
          setUnreadNotificationCount(0);
          return;
        }
        
        // Firebase Auth에 로그인되어 있을 때만 알림 구독 시작
        console.log("🔔 알림 구독 시작 (Firebase Auth 로그인 확인):", user.uid);
        
        unsubscribeUnreadCount = subscribeToUnreadNotificationCount(
      user.uid,
      count => {
        setUnreadNotificationCount(count);
      },
      error => {
            // 권한 오류는 조용히 처리하고 구독 취소
            if (error.message?.includes("permission") || error.code === "permission-denied") {
              console.warn("⚠️ 알림 구독 권한 오류 - 구독 중단");
              setUnreadNotificationCount(0);
              // 구독 취소는 subscribeToUnreadNotificationCount 내부에서 처리됨
            } else {
              console.error("❌ 읽지 않은 알림 개수 구독 오류:", error);
      }
          }
        );
      } catch (error) {
        console.warn("⚠️ Firebase Auth 확인 실패 - 알림 구독 건너뜀:", error);
        setUnreadNotificationCount(0);
      }
    }, 500); // 0.5초 대기하여 로그인 상태 안정화

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (unsubscribeUnreadCount) {
        console.log("🔕 알림 구독 종료");
      unsubscribeUnreadCount();
      }
    };
  }, [user?.uid, loading]); // loading 상태도 의존성에 추가


  return (
    <header 
      className="bg-white sticky top-0 z-50"
      style={{
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-5">
        <div className="flex justify-between items-center h-12 md:h-14">
          {/* Left: Logo */}
          <Link href="/" className="flex items-center gap-0">
            <img
              src="/톤1.png"
              alt="톤마켓"
              className="w-auto object-cover"
              style={{ height: '44px' }}
            />
            <img
              src="/톤2.png"
              alt="톤마켓"
              className="w-auto object-cover -ml-1"
              style={{ height: '36px' }}
            />
          </Link>

          {/* Center: Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-10">
            <Link
              href="/list"
              className="text-gray-700 hover:text-[#7A56E8] transition-colors font-semibold"
            >
              악기 구매하기
            </Link>
            <Link
              href="/product/new"
              className="text-gray-700 hover:text-[#7A56E8] transition-colors font-semibold"
            >
              악기 판매하기
            </Link>
            <Link
              href="/chat"
              className="text-gray-700 hover:text-[#7A56E8] transition-colors font-semibold"
            >
              채팅
            </Link>
          </nav>

          {/* Right: Notifications & Auth */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {user || session?.user ? (
              <>
                {/* 알림 버튼 */}
                <div className="relative">
                  <button
                    ref={notificationButtonRef}
                    onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
                    className="relative p-2 rounded-full hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                    aria-label="알림"
                  >
                    <Bell className="w-5 h-5 md:w-6 md:h-6 text-[#474747]" />
                    {unreadNotificationCount > 0 && (
                      <span className="absolute top-0.5 right-0.5 bg-[#7A56E8] text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5">
                        {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
                      </span>
                    )}
                  </button>

                  {/* 알림 드롭다운 */}
                  {showNotificationDropdown && user?.uid && (
                    <NotificationDropdown
                      userId={user.uid}
                      onClose={() => setShowNotificationDropdown(false)}
                    />
                  )}
                </div>

                {/* 프로필 드롭다운 (웹에서만 표시) */}
                <div className="hidden md:block">
                  <ProfileDropdown />
                </div>
              </>
            ) : (
              <div className="hidden md:flex items-center space-x-3">
                <Link
                  href="/auth/login"
                  className="text-gray-700 hover:text-[#7A56E8] transition-colors font-medium text-sm"
                >
                  로그인
                </Link>
                <Link
                  href="/auth/signup"
                  className="bg-[#7A56E8] text-white px-4 py-2 rounded-lg hover:bg-[#6D46D1] transition-colors font-medium text-sm"
                >
                  회원가입
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
